import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CreditCard, Square } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BUSINESS_CARD_STYLES, CARD_QUANTITIES } from "../data/site";

const ICONS = { "Rectangular Style": CreditCard, "Square Style": Square };

const CardStyle = ({ style }) => {
  const [qty, setQty] = useState(25);
  const Icon = ICONS[style.name] || CreditCard;
  const slug = style.name.toLowerCase().replace(/\s+/g, "-");

  return (
    <div
      data-testid={`card-style-${slug}`}
      className="brutal-border brutal-shadow bg-white p-7 sm:p-9 flex flex-col"
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-[#EAF1F8] text-[#3E6FA8] flex items-center justify-center">
          <Icon size={26} strokeWidth={2} />
        </div>
        <div>
          <h2 className="font-display text-2xl sm:text-3xl text-[#1F2530] leading-none">
            {style.name}
          </h2>
          <p className="text-sm font-semibold uppercase tracking-wider text-[#3E6FA8] mt-1">
            From ${style.prices[25]} / 25 cards
          </p>
        </div>
      </div>

      <p className="mt-5 leading-relaxed text-[#1F2530]/70">{style.desc}</p>

      <div className="mt-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2530]/60 mb-3">
          Select quantity
        </p>
        <div className="flex flex-wrap gap-2">
          {CARD_QUANTITIES.map((q) => (
            <button
              key={q}
              data-testid={`qty-${slug}-${q}`}
              onClick={() => setQty(q)}
              className={`min-w-[56px] px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                qty === q
                  ? "bg-[#3E6FA8] text-white border-[#3E6FA8]"
                  : "bg-white text-[#1F2530] border-[#E3E5E9] hover:border-[#3E6FA8] hover:text-[#3E6FA8]"
              }`}
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-7 pt-6 border-t border-[#E3E5E9] flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2530]/60">
            {qty} cards
          </p>
          <p
            data-testid={`price-${slug}`}
            className="inline-block mt-2 text-base font-bold uppercase tracking-wider bg-[#EAF1F8] text-[#3E6FA8] px-3 py-1.5 rounded-full"
          >
            ${style.prices[qty]}
          </p>
        </div>
        <Link
          to="/contact"
          state={{ service: `Business Cards — ${style.name} (qty ${qty})` }}
          data-testid={`card-order-${slug}`}
          className="shrink-0 inline-flex items-center gap-2 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider text-sm px-5 py-3"
        >
          Order <ArrowRight size={18} strokeWidth={2.5} />
        </Link>
      </div>
    </div>
  );
};

export default function BusinessCards() {
  return (
    <div className="App">
      <Navbar />

      {/* HEADER */}
      <section className="bg-[#1F2530] text-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Link
            to="/"
            data-testid="business-cards-back"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#F3F4F6]/70 hover:text-[#7BA7D9] transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={2.5} /> Back home
          </Link>
          <span className="block mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#7BA7D9]">
            Business Cards
          </span>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mt-3">
            Business Card Designs
          </h1>
          <p className="mt-5 max-w-2xl text-lg font-medium text-[#F3F4F6]/70">
            Choose the style that fits your brand, then pick your quantity to see
            pricing instantly. Rectangular cards start at{" "}
            <span className="font-bold text-[#7BA7D9]">$49</span> and square cards start
            at <span className="font-bold text-[#7BA7D9]">$59</span> for 25 cards.
          </p>
        </div>
      </section>

      {/* CARD STYLES */}
      <section className="bg-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
            data-testid="card-styles-grid"
          >
            {BUSINESS_CARD_STYLES.map((s) => (
              <CardStyle key={s.name} style={s} />
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 brutal-border brutal-shadow bg-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-[#1F2530]">
                Need a custom design?
              </h2>
              <p className="mt-2 font-medium text-[#1F2530]/70">
                Tell us about your brand and we'll design business cards that fit
                perfectly.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="business-cards-cta"
              className="shrink-0 inline-flex items-center gap-3 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-7 py-4"
            >
              Start Your Order <ArrowRight size={20} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
